import * as React from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { Button, ConfirmDialog, Menu, useToast, type MenuItemSpec } from "@ozpanel/ui";

// actions.tsx — ONE definition of "an action you can perform on an object", and one place
// that runs it. Built after three screens had each hand-wired their own buttons differently
// (a tenant's Suspend, an app's Start/Restart/Stop, Backups' Run now), none of them
// confirmed anything, none of them reported success or failure, and 49 of the backend's 56
// write routes for these objects had no UI at all.
//
// `danger` mirrors the backend's OWN per-route declaration (apidoc.Route.Danger, already in
// the OpenAPI spec as "Danger: high" — 14 of these routes carry it). It is not a styling
// choice made here: high means the server considers this destructive, so the UI confirms it
// and colours it, always, without each screen having to remember.
//
// The runner is deliberately NOT bound to a single object: a list renders one action set
// across many rows, so the object is passed at `start()` time, not at hook time. Binding it
// per-hook was the first version and could not express a list at all.

export type Danger = "low" | "medium" | "high";

export interface ActionConfirm {
  title: string;
  /** Rendered inside the dialog. Prefer the SERVER's own account of what will happen
   *  (e.g. the teardown preview) over a generic "are you sure". */
  body?: React.ReactNode;
  confirmLabel?: string;
}

export interface ActionDef<T> {
  key: string;
  label: string;
  /** Mirrors the backend's declared Danger for the route this calls. "high" ⇒ always
   *  confirmed, always styled destructive — no screen can forget to. */
  danger?: Danger;
  /** Extra confirmation copy/preview. A "high" action is confirmed even without this. */
  confirm?: ActionConfirm | ((obj: T) => ActionConfirm);
  /** Hide the action when it makes no sense for this object (Suspend on a suspended
   *  tenant). Hidden, not disabled-and-unexplained. */
  available?(obj: T): boolean;
  run(obj: T): Promise<unknown>;
  /** Query keys to refetch after success, so the screen reflects the new truth rather than
   *  the state it was rendered with. */
  invalidates?(obj: T): QueryKey[];
  /** Message shown on success. Falls back to "<label> — done". */
  successMessage?(obj: T, result: unknown): string;
  /** Run after a successful action — e.g. navigate away after a delete. */
  onDone?(obj: T, result: unknown): void;
}

export function isDestructive<T>(a: ActionDef<T>): boolean {
  return a.danger === "high";
}

function confirmFor<T>(a: ActionDef<T>, obj: T): ActionConfirm | undefined {
  return typeof a.confirm === "function" ? a.confirm(obj) : a.confirm;
}

function needsConfirm<T>(a: ActionDef<T>): boolean {
  return a.danger === "high" || !!a.confirm;
}

interface Pending<T> {
  action: ActionDef<T>;
  obj: T;
}

/**
 * useActionRunner wires ActionDefs to react-query + toast + the confirm dialog. Returns a
 * `start(action, obj)` the caller invokes, plus the dialog element to render. One instance
 * drives every action on a screen — detail buttons and every row's menu alike — so pending
 * and confirm state cannot get out of step between them.
 */
export function useActionRunner<T>() {
  const qc = useQueryClient();
  const { notify } = useToast();
  const [awaiting, setAwaiting] = React.useState<Pending<T> | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ action, obj }: Pending<T>) => ({ action, obj, result: await action.run(obj) }),
    onSuccess: ({ action, obj, result }) => {
      for (const key of action.invalidates?.(obj) ?? []) {
        void qc.invalidateQueries({ queryKey: key });
      }
      notify({
        kind: "success",
        title: action.successMessage?.(obj, result) ?? `${action.label} — done`,
      });
      setAwaiting(null);
      action.onDone?.(obj, result);
    },
    onError: (err, { action }) => {
      // Stays on screen until dismissed (Toast's own rule for errors) and carries the
      // server's message — "name a tenant with ?tenant=<id>" is actionable, "failed" is not.
      notify({
        kind: "error",
        title: `${action.label} failed`,
        body: err instanceof Error ? err.message : String(err),
      });
      setAwaiting(null);
    },
  });

  const start = React.useCallback(
    (action: ActionDef<T>, obj: T) => {
      if (needsConfirm(action)) {
        setAwaiting({ action, obj });
        return;
      }
      mutation.mutate({ action, obj });
    },
    [mutation],
  );

  const awaitingConfirm = awaiting ? confirmFor(awaiting.action, awaiting.obj) : undefined;

  const dialog = awaiting ? (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) setAwaiting(null);
      }}
      title={awaitingConfirm?.title ?? `${awaiting.action.label}?`}
      body={awaitingConfirm?.body}
      confirmLabel={awaitingConfirm?.confirmLabel ?? awaiting.action.label}
      destructive={isDestructive(awaiting.action)}
      pending={mutation.isPending}
      onConfirm={() => mutation.mutate(awaiting)}
    />
  ) : null;

  return {
    start,
    dialog,
    isPending: mutation.isPending,
    pendingKey: mutation.isPending ? mutation.variables?.action.key : undefined,
  };
}

export type ActionRunner<T> = ReturnType<typeof useActionRunner<T>>;

export function visibleActions<T>(actions: ActionDef<T>[], obj: T): ActionDef<T>[] {
  return actions.filter((a) => a.available?.(obj) ?? true);
}

/**
 * ActionBar — the detail-page action surface. Non-destructive actions first as ordinary
 * buttons; destructive ones last and visibly destructive, so "Delete" is never adjacent to
 * and identical with "Restart".
 */
export function ActionBar<T>({
  actions,
  obj,
  runner,
}: {
  actions: ActionDef<T>[];
  obj: T;
  runner: ActionRunner<T>;
}) {
  const visible = visibleActions(actions, obj);
  if (visible.length === 0) return null;
  const safe = visible.filter((a) => !isDestructive(a));
  const destructive = visible.filter(isDestructive);
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {safe.map((a) => (
        <Button key={a.key} variant="default" disabled={runner.isPending} onClick={() => runner.start(a, obj)}>
          {runner.pendingKey === a.key ? "Working…" : a.label}
        </Button>
      ))}
      {destructive.map((a) => (
        <Button key={a.key} variant="danger" disabled={runner.isPending} onClick={() => runner.start(a, obj)}>
          {runner.pendingKey === a.key ? "Working…" : a.label}
        </Button>
      ))}
    </div>
  );
}

/** RowActions — the same ActionDefs, collapsed into a per-row menu for lists. */
export function RowActions<T>({
  actions,
  obj,
  runner,
}: {
  actions: ActionDef<T>[];
  obj: T;
  runner: ActionRunner<T>;
}) {
  const items: MenuItemSpec[] = visibleActions(actions, obj).map((a) => ({
    key: a.key,
    label: a.label,
    destructive: isDestructive(a),
    disabled: runner.isPending,
    onSelect: () => runner.start(a, obj),
  }));
  return <Menu items={items} />;
}
